// redux/litshield/litshieldSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { checkLITShieldAccessApi, enterLITShieldAccessApi, logoutMultipleLITShieldScopesApi } from "../../services/litshield/litshieldApi";

interface ScopeAccess {
  access_granted: boolean;
  expires_at: string | null;
  pin_security_enabled: boolean;
}

interface LitshieldState {
  scopes: {
    [scope: string]: ScopeAccess; 
  };
}

const initialState: LitshieldState = {
  scopes: {},
};

// ✅ Async thunk to fetch access info for a specific scope
export const fetchScopeAccess = createAsyncThunk(
  "litshield/fetchScopeAccess",
  async (scope: string) => {
    const result = await checkLITShieldAccessApi(scope);
    return {
      scope,
      access_granted: result.access_granted,
      expires_at: result.expires_at,
      pin_security_enabled: result.pin_security_enabled ?? true, // fallback if not returned
    };
  }
);

// ✅ Async thunk to enter scope with PIN
export const enterScopeAccess = createAsyncThunk(
  "litshield/enterScopeAccess",
  async ({ scope, pin }: { scope: string; pin: string }, { rejectWithValue }) => {
    try {
      const result = await enterLITShieldAccessApi(scope, pin);
      return {
        scope,
        access_granted: true,
        expires_at: result.expires_at,
        pin_security_enabled: result.pin_security_enabled ?? true,
      };
    } catch (error: any) {
      return rejectWithValue({ scope, error: error.message || "PIN submission failed" });
    }
  }
);

// ✅ Async thunk to logout from all LITShield scopes
export const logoutAllLITShieldScopes = createAsyncThunk(
  "litshield/logoutAllLITShieldScopes",
  async (_, { dispatch }) => {
    const scopes = ["conversation", "covenant"];
    await logoutMultipleLITShieldScopesApi(scopes);
    dispatch(clearScopeAccess("conversation"));
    dispatch(clearScopeAccess("covenant"));
    return true;
  }
);




const litshieldSlice = createSlice({
  name: "litshield",
  initialState,
  reducers: {
    // ✅ Manually set access status for a scope
    setScopeAccess: (
      state,
      action: PayloadAction<{
        scope: string;
        access_granted: boolean;
        expires_at: string | null;
        pin_security_enabled: boolean;
      }>
    ) => {
      const { scope, access_granted, expires_at, pin_security_enabled } = action.payload;
      state.scopes[scope] = { access_granted, expires_at, pin_security_enabled };
    },

    // ✅ Remove access info for a scope
    clearScopeAccess: (state, action: PayloadAction<string>) => {
      delete state.scopes[action.payload];
    },

    // ✅ Clear all scopes
    clearAllScopeAccess: (state) => {
      state.scopes = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchScopeAccess.fulfilled, (state, action) => {
        const { scope, access_granted, expires_at, pin_security_enabled } = action.payload;
        state.scopes[scope] = { access_granted, expires_at, pin_security_enabled };
      })
      .addCase(enterScopeAccess.fulfilled, (state, action) => {
        const { scope, access_granted, expires_at, pin_security_enabled } = action.payload;
        state.scopes[scope] = { access_granted, expires_at, pin_security_enabled };
      })
      .addCase(logoutAllLITShieldScopes.fulfilled, (state) => {
      });
  },
});

export const {
  setScopeAccess,
  clearScopeAccess,
  clearAllScopeAccess,
} = litshieldSlice.actions;

export default litshieldSlice.reducer;
